// My events — when signed in, shows what the user hosts, has registered for,
// and their certificates (via getMyActivity + their standalone events). When
// signed out, prompts sign-in. Replaces the old registration-ID lookup screen.
import { useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/layout/Shell";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import Badge from "@/components/Badge";
import { fmtDate, cn } from "@/lib/utils";
import {
  Calendar, MapPin, Ticket, Award, Megaphone, Star, ArrowRight,
  Users, ExternalLink, MessageCircle, LogIn, CalendarDays,
} from "lucide-react";

const TYPE_ICON = {
  seminar: "Seminar", workshop: "Workshop", talk: "Talk", hackathon: "Hackathon",
  placement: "Placement", cultural: "Cultural", conference: "Conference", other: "Event",
};

/* ── Signed-out prompt ──────────────────────────────────────────── */
function SignedOut() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 text-center bg-background">
      <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/15 flex items-center justify-center">
        <Star className="h-6 w-6 text-primary" />
      </div>
      <div>
        <h1 className="text-xl font-extrabold tracking-tight">Your events, in one place</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-sm">
          Sign in with your @bmsit.in email to see events you host, the ones you've
          registered for, and your certificates.
        </p>
      </div>
      <div className="flex gap-3">
        <Link to="/login">
          <button className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity">
            <LogIn className="h-4 w-4" />Sign in
          </button>
        </Link>
        <Link to="/events">
          <button className="inline-flex items-center gap-2 h-10 px-5 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition-colors">
            Browse public events
          </button>
        </Link>
      </div>
    </div>
  );
}

/* ── Cards ──────────────────────────────────────────────────────── */
function MetaRow({ date, venue, extra }) {
  return (
    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
      {date   && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(date)}</span>}
      {venue  && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{venue}</span>}
      {extra}
    </div>
  );
}

function LinkBtn({ to, href, children, tone = "muted" }) {
  const cls = cn(
    "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors",
    tone === "primary" ? "bg-primary/10 text-primary hover:bg-primary/20"
                       : "bg-muted text-foreground/80 hover:bg-muted/70 border border-border/60"
  );
  if (to)  return <Link to={to} className={cls}>{children}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{children}</a>;
}

function HostingCard({ item }) {
  const title = item.eventTitle || item.title || item.eventName || item.bookingID;
  const venue = item.hallName || item.venue || "";
  const date  = item.date || item.eventDate || "";
  const published = item.hasEvent || item.publishedAt || item.isStandalone;
  return (
    <div className="surface rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary/80">
              {TYPE_ICON[item.eventType] || "Event"}{item.isStandalone ? " · standalone" : ""}
            </span>
            {item.asCoordinator && (
              <span className="text-[10px] font-semibold text-violet-600 bg-violet-500/12 px-1.5 py-0.5 rounded-full">Coordinator</span>
            )}
          </div>
          <div className="font-semibold text-sm truncate mt-0.5">{title}</div>
          <MetaRow date={date} venue={venue}
            extra={<span className="font-mono">{item.bookingID}</span>} />
        </div>
        {item.status ? <Badge status={item.status} /> :
          published ? <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-500/12 px-2 py-0.5 rounded-full">Published</span>
                    : <span className="text-[10px] font-semibold text-amber-600 bg-amber-500/12 px-2 py-0.5 rounded-full">Draft</span>}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {!item.isStandalone && (
          <LinkBtn to={`/portal/events?bid=${item.bookingID}`} tone="primary">
            <Megaphone className="h-3.5 w-3.5" />{item.hasEvent ? "Manage event" : "Set up event"}
          </LinkBtn>
        )}
        {(item.hasEvent || item.isStandalone) && (
          <LinkBtn href={`/events/${item.bookingID}`}>
            <ExternalLink className="h-3.5 w-3.5" />View page
          </LinkBtn>
        )}
      </div>
    </div>
  );
}

function RegisteredCard({ r }) {
  const sl = r.waitlistPos ? `Waitlist #${r.waitlistPos}` : null;
  return (
    <div className="surface rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary/80">
            {TYPE_ICON[r.eventType] || "Event"}
          </span>
          <div className="font-semibold text-sm truncate mt-0.5">{r.eventTitle || r.bookingID}</div>
          <MetaRow date={r.eventDate} venue={r.hallName}
            extra={<span className="font-mono">{r.regID}</span>} />
        </div>
        <Badge status={r.status} />
      </div>
      {sl && <div className="text-[11px] text-blue-600 dark:text-blue-400 mt-1">{sl}</div>}
      <div className="flex flex-wrap gap-2 mt-3">
        {r.status === "confirmed" && r.ticketUrl && (
          <LinkBtn href={r.ticketUrl} tone="primary"><Ticket className="h-3.5 w-3.5" />Ticket</LinkBtn>
        )}
        <LinkBtn href={r.eventPageUrl || `/events/${r.bookingID}`}><ExternalLink className="h-3.5 w-3.5" />Event</LinkBtn>
        {r.hasCertificate && r.certVerifyUrl && (
          <LinkBtn href={r.certVerifyUrl}><Award className="h-3.5 w-3.5" />Certificate</LinkBtn>
        )}
        {!r.hasFeedback && r.feedbackUrl && (
          <LinkBtn href={r.feedbackUrl}><MessageCircle className="h-3.5 w-3.5" />Feedback</LinkBtn>
        )}
      </div>
    </div>
  );
}

function CertCard({ c }) {
  return (
    <div className="surface rounded-2xl p-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-violet-600 shrink-0" />
          <div className="font-semibold text-sm truncate">{c.eventTitle}</div>
        </div>
        <MetaRow date={c.eventDate}
          extra={<span>{c.daysAttended} day{c.daysAttended === 1 ? "" : "s"} attended</span>} />
      </div>
      {c.verifyUrl && <LinkBtn href={c.verifyUrl} tone="primary">Verify<ArrowRight className="h-3.5 w-3.5" /></LinkBtn>}
    </div>
  );
}

function Empty({ icon: Icon, title, sub, to, cta }) {
  return (
    <div className="text-center py-12 px-4">
      <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
      {to && <Link to={to}><button className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-primary text-primary-foreground text-xs font-semibold">{cta}</button></Link>}
    </div>
  );
}

/* ── Signed-in view ─────────────────────────────────────────────── */
function SignedIn() {
  const [tab, setTab] = useState("hosting");
  const activity  = useFetch(() => api.myActivity(), []);
  const standalone = useFetch(() => api.myStandaloneEvents().then(r => r?.events || []).catch(() => []), []);

  const a = activity.data || {};
  const hosting = [...(a.organiserBookings || []), ...(standalone.data || [])];
  const regs    = a.participantRegs || [];
  const certs   = a.certificates || [];
  const loading = activity.loading;

  const TABS = [
    ["hosting",  "Hosting",    hosting.length, Megaphone],
    ["registered","Registered", regs.length,   Ticket],
    ["certs",    "Certificates", certs.length, Award],
  ];

  return (
    <Shell role="portal" title="My events" subtitle="Events you host, the ones you've registered for, and your certificates">
      <div className="max-w-2xl space-y-4">
        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-muted w-full sm:w-auto sm:inline-flex">
          {TABS.map(([id, label, count, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              className={cn("flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all",
                tab === id ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground")}>
              <Icon className="h-3.5 w-3.5" />{label}
              <span className={cn("ml-0.5 text-[10px] px-1.5 rounded-full", tab === id ? "bg-primary/10 text-primary" : "bg-muted-foreground/10")}>{count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-2xl shimmer" />)}</div>
        ) : (
          <div className="space-y-3">
            {tab === "hosting" && (hosting.length
              ? hosting.map((h, i) => <HostingCard key={h.bookingID || h.eventID || i} item={h} />)
              : <Empty icon={Megaphone} title="You're not hosting anything yet"
                  sub="Book a hall or create a standalone event to get started." to="/portal/book" cta="Book a hall" />)}

            {tab === "registered" && (regs.length
              ? regs.map((r, i) => <RegisteredCard key={r.regID || i} r={r} />)
              : <Empty icon={Ticket} title="No registrations yet"
                  sub="Events you register for will show up here." to="/events" cta="Browse events" />)}

            {tab === "certs" && (certs.length
              ? certs.map((c, i) => <CertCard key={c.certID || i} c={c} />)
              : <Empty icon={Award} title="No certificates yet"
                  sub="Certificates are issued after you attend an event." />)}
          </div>
        )}
      </div>
    </Shell>
  );
}

export default function MyEvents() {
  const { isAuthed } = useAuth();
  return isAuthed ? <SignedIn /> : <SignedOut />;
}
